import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useCreateRoom, useRooms, useRoomsDashboard, useTenantPlan } from '@/api/hooks';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { SubscriberGuide, TrialGuide } from '@/components/FirstRunGuides';
import { countLabel } from '@/lib/engagement';
import { isNewPaidSubscriber, NEW_ROOM_NAME_INPUT_ID } from '@/lib/guides';
import { planLimitOf } from '@/lib/plan';
import { LIFECYCLE_LABELS, lifecycleChip, type RoomLocationState } from '@/lib/room';
import { checklistHref, sortActiveRooms } from '@/lib/rooms-dashboard';
import {
  ApiError,
  Button,
  DEFAULT_ROOM_TIMEZONE,
  FISCAL_YEAR_END_MONTHS,
  fiscalYearEndLabel,
  timeZoneOptions,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChecklistSummary,
  Input,
  ROOM_ADMIN_ROLES,
  ROOM_ADMIN_ROLE_HINTS,
  ROOM_ADMIN_ROLE_LABELS,
  Chip,
  cn,
  containerClass,
  formatDate,
  HelpTip,
  PageTitle,
} from '@dataroom/shared';
import type { Room, RoomAdminRole, RoomDashboardEntry } from '@dataroom/shared';

export function RoomsPage() {
  const rooms = useRooms();
  // Per-room deal figures (rooms the caller administers). A failure here never
  // hides the list — the rooms just show without figures.
  const dashboard = useRoomsDashboard();
  const createRoom = useCreateRoom();
  const plan = useTenantPlan();
  // Only a tenant admin may create rooms. A room CO-ADMIN (ADR-0049) lists just the
  // rooms they administer and is refused the tenant-admin plan read (403) — the
  // same capability signal the nav uses — so the create card is hidden for them.
  const canCreateRooms = !(plan.error instanceof ApiError && plan.error.status === 403);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [adminRole, setAdminRole] = useState<RoomAdminRole>('intermediary');
  // The room's fiscal calendar (ADR-0040): the checklist's period requests are
  // generated from it, so it is chosen here, before the checklist is set up.
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState(12);
  const [timezone, setTimezone] = useState(DEFAULT_ROOM_TIMEZONE);

  if (rooms.error instanceof ApiError && rooms.error.status === 401) {
    return <Navigate to="/login" replace />;
  }

  // A plan-limit 409 (active_limit_reached) gets the options dialog, not a raw error.
  const limit = planLimitOf(createRoom.error);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    // Land on the new room and open the checklist-source dialog there (owner ask:
    // pick default / upload / saved checklist right after creating a room).
    createRoom.mutate(
      { name: trimmed, adminRole, fiscalYearEndMonth, timezone },
      {
        onSuccess: (room) => {
          setName('');
          const state: RoomLocationState = { setupChecklist: true };
          navigate(`/rooms/${room.id}`, { state });
        },
      },
    );
  }

  // Active rooms first (the retired `draft` reads as active), as the deal
  // dashboard; everything else the listing returns (archived, and the retired
  // `closed` state the API still maps to archived) goes in its own card, without
  // figures.
  const figures = new Map((dashboard.data ?? []).map((e) => [e.roomId, e] as const));
  const isActive = (r: Room) => r.lifecycleState === 'active' || r.lifecycleState === 'draft';
  const activeRooms = sortActiveRooms((rooms.data ?? []).filter(isActive), figures);
  const archivedRooms = (rooms.data ?? []).filter((r) => !isActive(r));

  return (
    <div className={cn(containerClass, 'space-y-6 py-6')}>
      <div className="flex items-center gap-2">
        <PageTitle>Rooms</PageTitle>
        <HelpTip id="admin.rooms.rooms" />
      </div>
      {rooms.data && plan.data?.isTrial ? <TrialGuide rooms={rooms.data} /> : null}
      {rooms.data && plan.data && isNewPaidSubscriber(plan.data) ? (
        <SubscriberGuide rooms={rooms.data} plan={plan.data} />
      ) : null}

      {rooms.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rooms.error ? (
        <p className="text-sm text-destructive">{rooms.error.message}</p>
      ) : (
        <ActiveRoomsCard rooms={activeRooms} figures={figures} />
      )}

      {canCreateRooms ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Create a room <HelpTip id="admin.rooms.create" className="ml-1" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={submit}>
              <div className="flex gap-2">
                <Input
                  id={NEW_ROOM_NAME_INPUT_ID}
                  placeholder="Room name (e.g. Project Atlas)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label="Room name"
                />
                <Button type="submit" disabled={createRoom.isPending}>
                  {createRoom.isPending ? 'Creating…' : 'Create'}
                </Button>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1">
                  <label htmlFor="new-room-role" className="text-sm font-medium">
                    Admin role
                  </label>
                  <HelpTip id="admin.rooms.admin-role" className="ml-1" />
                </div>
                <select
                  id="new-room-role"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  value={adminRole}
                  onChange={(e) => setAdminRole(e.target.value as RoomAdminRole)}
                  disabled={createRoom.isPending}
                >
                  {ROOM_ADMIN_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {ROOM_ADMIN_ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">{ROOM_ADMIN_ROLE_HINTS[adminRole]}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="new-room-fye" className="text-sm font-medium">
                    Fiscal year end
                  </label>
                  <select
                    id="new-room-fye"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    value={fiscalYearEndMonth}
                    onChange={(e) => setFiscalYearEndMonth(Number(e.target.value))}
                    disabled={createRoom.isPending}
                  >
                    {FISCAL_YEAR_END_MONTHS.map((month) => (
                      <option key={month} value={month}>
                        {fiscalYearEndLabel(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="new-room-tz" className="text-sm font-medium">
                    Timezone
                  </label>
                  <select
                    id="new-room-tz"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    disabled={createRoom.isPending}
                  >
                    {timeZoneOptions(timezone).map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                The checklist&apos;s yearly, quarterly and year-to-date requests follow the
                company&apos;s fiscal year (e.g. June 30 → &ldquo;FY2025&rdquo; = July 2024 – June 2025).
                You can change it until the checklist is created.
              </p>
            </form>
            {createRoom.error && !limit ? (
              <p className="mt-2 text-sm text-destructive">{createRoom.error.message}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      {limit ? <PlanLimitDialog info={limit} onClose={() => createRoom.reset()} /> : null}

      {!rooms.isPending && !rooms.error && archivedRooms.length > 0 ? (
        <RoomListCard
          title="Archived rooms"
          rooms={archivedRooms}
          empty=""
          description="Read-only for you; buyers and sellers no longer have access."
        />
      ) : null}
    </div>
  );
}

/**
 * The active rooms as a multi-deal dashboard (owner ask 2026-10-07): per room the
 * caller administers, the checklist at a glance — resolved of total, covered,
 * signed "doesn't apply", still missing — then documents to review, buyer
 * questions waiting on the intermediary, and buyers with their latest activity.
 * Each figure links to the page it comes from; the room name and "Still missing"
 * land on the room's still-due checklist. Facts only, most still missing first.
 */
function ActiveRoomsCard({
  rooms,
  figures,
}: {
  rooms: Room[];
  figures: ReadonlyMap<string, RoomDashboardEntry>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Active rooms{' '}
          <span className="font-sans text-sm font-normal text-muted-foreground">
            ({rooms.length})
          </span>
          <HelpTip id="admin.rooms.active" className="ml-2" />
        </CardTitle>
        {rooms.length > 1 && figures.size > 0 ? (
          <p className="text-xs text-ink-3">Most items still missing first.</p>
        ) : null}
      </CardHeader>
      <CardContent>
        {rooms.length > 0 ? (
          <ul className="divide-y">
            {rooms.map((room, index) => (
              <ActiveRoomRow
                key={room.id}
                room={room}
                entry={figures.get(room.id)}
                showTips={index === 0}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No active rooms. Create one below.</p>
        )}
      </CardContent>
    </Card>
  );
}

function ActiveRoomRow({
  room,
  entry,
  showTips,
}: {
  room: Room;
  entry: RoomDashboardEntry | undefined;
  /** The "?" on the checklist figures: on the first room only, not every row. */
  showTips: boolean;
}) {
  const hasChecklist = (entry?.checklist.total ?? 0) > 0;
  const lifecycle = lifecycleChip(room.lifecycleState);
  const factLink =
    'inline-flex min-h-tap items-center text-ink-2 underline-offset-2 hover:text-ink hover:underline md:min-h-0';
  return (
    <li className="space-y-3 py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={hasChecklist ? checklistHref(room.id, 'missing') : `/rooms/${room.id}`}
            className="break-words font-semibold hover:underline"
          >
            {room.name}
          </Link>
          <p className="text-xs text-ink-3">Created {formatDate(room.createdAt)}</p>
        </div>
        <Chip tone={lifecycle.tone} icon={lifecycle.icon}>
          {LIFECYCLE_LABELS[room.lifecycleState] ?? room.lifecycleState}
        </Chip>
      </div>
      {entry ? (
        <>
          {hasChecklist ? (
            <div className="flex items-start gap-1">
              <ChecklistSummary
                size="compact"
                className="min-w-0 flex-1"
                counts={entry.checklist}
                audience="intermediary"
                renderLink={(filter, props) => (
                  <Link to={checklistHref(room.id, filter)} {...props} />
                )}
              />
              {showTips ? (
                <HelpTip id="admin.rooms.checklist-summary" className="mt-2" />
              ) : (
                <span aria-hidden="true" className="w-5 shrink-0" />
              )}
            </div>
          ) : (
            <p className="text-[13px] text-ink-2">
              No checklist yet.{' '}
              <Link
                to={`/rooms/${room.id}/checklist`}
                className="font-medium text-primary hover:underline"
              >
                Set up the checklist
              </Link>
            </p>
          )}
          <ul
            className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]"
            aria-label={`${room.name}: activity`}
          >
            {entry.documentsNeedingReview > 0 ? (
              <li>
                <Link to={`/rooms/${room.id}/review`} className={factLink}>
                  {countLabel(entry.documentsNeedingReview, 'document')} to review
                </Link>
              </li>
            ) : null}
            {entry.questionsNeedingAction > 0 ? (
              <li>
                <Link to={`/rooms/${room.id}/qa`} className={factLink}>
                  {countLabel(entry.questionsNeedingAction, 'buyer question')} waiting on you
                </Link>
              </li>
            ) : null}
            <li>
              <Link to={`/rooms/${room.id}/buyers`} className={factLink}>
                {entry.buyers > 0
                  ? `${countLabel(entry.buyers, 'buyer')} · last buyer activity ${formatDate(
                      entry.lastBuyerActivityAt,
                      'none yet',
                    )}`
                  : 'No buyers yet'}
              </Link>
            </li>
          </ul>
        </>
      ) : null}
    </li>
  );
}

function RoomListCard({
  title,
  description,
  rooms,
  empty,
}: {
  title: string;
  description?: string;
  rooms: Room[];
  empty: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title}{' '}
          <span className="font-sans text-sm font-normal text-muted-foreground">({rooms.length})</span>
        </CardTitle>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </CardHeader>
      <CardContent>
        {rooms.length > 0 ? (
          <ul className="divide-y">
            {rooms.map((room) => (
              <li key={room.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <Link to={`/rooms/${room.id}`} className="font-semibold hover:underline">
                    {room.name}
                  </Link>
                  <p className="text-xs text-ink-3">Created {formatDate(room.createdAt)}</p>
                </div>
                <Chip {...lifecycleChip(room.lifecycleState)}>
                  {LIFECYCLE_LABELS[room.lifecycleState] ?? room.lifecycleState}
                </Chip>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{empty}</p>
        )}
      </CardContent>
    </Card>
  );
}
